<template>
  <ButtonAddToCart
    v-if="isPurchasable"
    v-bind="$attrs"
    :prevent-animation="preventAnimation || !isReadyToAdd"
    :disabled="isDisabled"
    :on-action="onAddToCartClick"
  />
</template>

<script setup lang="ts">
import { computed } from 'vue';

import {
  addToCartClickOutcomes,
  addToCartClickSources
} from '@/shared/constants/constants';
import { useCartStore } from '@/shared/pinia/modules/cart';
import { useProductStore } from '@/shared/pinia/modules/product';
import { useProductOptionsStore } from '@/shared/pinia/modules/product-options';
import { useProductQuantitiesStore } from '@/shared/pinia/modules/product-quantities';
import {
  type AddToCartClickOutcome,
  type AddToCartClickSource,
  trackAddToCartClicked
} from '@/shared/tracking/add-to-cart-clicked';

import ButtonAddToCart from './ButtonAddToCart.vue';

defineOptions({ inheritAttrs: false });

const {
  onAfterAction,
  preventAnimation = false,
  source = addToCartClickSources.MAIN
} = defineProps<{
  onAfterAction?: () => void;
  preventAnimation?: boolean;
  source?: AddToCartClickSource;
}>();

const cartStore = useCartStore();
const productStore = useProductStore();
const quantityStore = useProductQuantitiesStore();
const optionsStore = useProductOptionsStore();

const isPurchasable = computed(() => productStore.purchasable);
const isDisabled = computed(
  () => productStore.isProduct && !optionsStore.productInStock
);
const isReadyToAdd = computed(
  () => optionsStore.isOptionSelected && quantityStore.quantity > 0
);

const trackClick = (outcome: AddToCartClickOutcome) =>
  trackAddToCartClicked({
    source,
    outcome,
    product_id: productStore.productId,
    variant_id:
      optionsStore.activeOption?.id ?? (Number(productStore.variantId) || null),
    quantity: quantityStore.quantity
  });

const onAddToCartClick = async () => {
  if (!optionsStore.isOptionSelected) {
    trackClick(addToCartClickOutcomes.OPTIONS_MODAL_OPENED);
    optionsStore.showOptionsModal = true;
    return;
  }
  await cartStore.addMainProductToCart();
  trackClick(addToCartClickOutcomes.ADDED);
  onAfterAction?.();
};
</script>
